import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ViewEncapsulation } from "@angular/core";

import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { finalize, map, takeUntil, tap } from "rxjs/operators";

import { AuthorizationService, ConfirmationDialogInputService, ConfirmationDialogService, NotificationPopupService } from '@myapp/modules/fwcore/services';
import { ListViewComponent, ListViewModule } from "@progress/kendo-angular-listview";
import { ControlResizerOptions, ControlResizerService } from "../../../../../../services/framework";
import { AdminService } from '../../../services';

import { NgClass, NgIf } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "@progress/kendo-angular-buttons";
import { CheckBoxModule } from "@progress/kendo-angular-inputs";
import { RequestDelete, RequestUserPermission } from "../../../../../modules/fwcore/models";
import { RequestRole, RequestRoleSet, Role, RoleChecked, RoleSave } from "../../../models";
import { RolesEditorComponent } from "../roles-editor/roles-editor.component";


@Component({
  selector: 'app-roles-list',
  templateUrl: './roles-list.component.html',
  styleUrls: ['./roles-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [NgIf, ButtonModule, ListViewModule, NgClass, FormsModule, CheckBoxModule, RolesEditorComponent]
})

export class RolesListComponent implements AfterViewInit, OnChanges, OnDestroy {

  /*-------------------------- Input / Outputs --------------------------------*/

  @Input() public moduleId: string | undefined | null = null;
  @Input() public licenseId: string | null = null;
  @Input() public userId: string | null = null;

  @Input() public showCheckbox: boolean = false;

  @Input() public showToolbar: boolean = false;
  @Input() public showSaveButton: boolean = false;
  @Input() public showReadOnlyButton: boolean = false;
  @Input() public showDelButton: boolean = false;
  @Input() public showTemplateButton: boolean = false;

  @Input() public minHeight: number = 200;
  @Input() public widthThreshold: number = 600;

  @Input()
  set checkedIds(ids: string[]) {
    this.dataItems.forEach((item) => {
      item.checked = ids.includes(item.id!);
    });
  }
  get checkedIds(): string[] {

    const checkedItemIds: string[] = this.dataItems
      .filter((item) => item.checked)
      .map((item) => item.id!);

    return checkedItemIds ?? [];
  }

  @Output() selected: EventEmitter<Role | null> = new EventEmitter();
  @Output() saveRolePermissionsClicked = new EventEmitter<boolean>();

  /*-------------------------- public properties / methods --------------------*/

  public selectedItem?: Role | null;

  public heightList: number = 200;

  public heightChange(options?: ControlResizerOptions): void {

    let ctrlOptions: ControlResizerOptions = new ControlResizerOptions();

    let boundClientRect = this.elem.nativeElement.getBoundingClientRect()
    ctrlOptions.elemTop = boundClientRect.top;

    if (options) {
      ctrlOptions = { ...options, elemTop: ctrlOptions.elemTop };
    }

    if (this.showToolbar) {

      let h = this.elem.nativeElement?.querySelector('.isg-rolelist-toolbar')?.getBoundingClientRect().height;

      if (h <= 0) {
        ctrlOptions.offsetTopHeight += 5;
      }
      else
        ctrlOptions.offsetTopHeight += h + 5;
    }

    if (window.innerWidth > this.widthThreshold)
      this.heightList = this.controlResizer.calculateHeight(ctrlOptions);
    else {
      // Do not dynamically resize height if below the width threshold
      this.heightList = this.minHeight;
    }

  }

  public clearSelection() {
    this.selectedItem = null;
    this.listview.focus(undefined);
  }

  public getCheckedNames(): string[] {

    const names: string[] = this.dataItems
      .filter((item) => item.checked)
      .map((item) => item.name!);

    return names;
  }

  public getCheckedDataItems(): Role[] {

    const items: Role[] = this.dataItems
      .filter((item) => item.checked);

    return items;
  }

  /*-------------------------- Internal properties  ---------------------------*/

  @ViewChild(ListViewComponent, { static: false })
  protected listview!: ListViewComponent;

  protected loading: boolean = true;
  protected dataItems: RoleChecked[] = [];

  protected showEditor: boolean = false;
  protected isViewOnlyEditor: boolean = true;

  protected destroy$ = new Subject();

  protected reqPerm: RequestUserPermission = {

  }

  protected editDataItem: Role | null | undefined;

  /*-------------------------- Constructor  --------------------------*/

  constructor(protected router: Router
    , protected serviceAuth: AuthorizationService
    , protected elem: ElementRef
    , protected controlResizer: ControlResizerService
    , protected adminService: AdminService
    , protected dialogConfirm: ConfirmationDialogService
    , protected dialogConfirmInput: ConfirmationDialogInputService
    , protected notification: NotificationPopupService

  ) {
  }

  /*----------------------------------- ng  --------------------------*/

  ngAfterViewInit(): void {
    this.reqPerm.route = this.router.url;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.moduleId && !changes.moduleId.firstChange) {
      this.getRoles();
    }

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*--------------------------- event handlers  --------------------------*/

  protected onAddClick(): void {
    this.isViewOnlyEditor = false;
    this.setShowEditor(true);
  }

  protected onEditClick() {

    this.isViewOnlyEditor = false;
    this.setShowEditor(true);
    this.editDataItem = this.selectedItem;
  }

  protected onViewClick() {

    this.isViewOnlyEditor = true;
    this.setShowEditor(true);
    this.editDataItem = this.selectedItem;
  }


  protected onCopyClick() {

    const txt = "Please specify a new name for the duplicate role '"
      + this.selectedItem!.name
      + "'. You can add/remove permissions and will need to assign users afterwards."
      ;

    this.dialogConfirmInput.open("Copy Role"
      , txt).
      subscribe((result: any) => {

        if (result.success) {
          this.copyRole(this.selectedItem!, result.value);
        }

      });

  }

  protected onTemplateClick() {

    const txt = "Please specify a name for the template. This will be created with the same permissions as the role '"
      + this.selectedItem!.name
      + "' and will be available for all companies to select when creating new roles.";

    this.dialogConfirmInput.open("Confirm Template Creation"
      , txt).
      subscribe((result: any) => {

        if (result.success) {
          this.createTemplateFromRole(this.selectedItem!, result.value);
        }

      });
  }

  protected onSaveRolePermissionsClick() {
    this.saveRolePermissionsClicked.emit(true);
  }

  protected onDeleteClick(): void {
    this.dialogConfirm.open('Confirm Deletion', 'Are you sure you want to delete this role?')
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result.value && this.selectedItem?.id) {
          this.deleteRole(this.selectedItem.id);
        }
      });
  }

  protected onCancelEditor(): void {
    this.setShowEditor(false);
    this.editDataItem = null;
  }

  protected onSaveEditor(item: RoleSave): void {

    item.licenseId = this.licenseId;

    this.setRole(item);
    this.setShowEditor(false);
  }

  protected onClickItemEvent(index: number, dataItem: Role, event: Event): void {

    let newSelection: Role = dataItem;

    if (this.selectedItem == newSelection) {
      this.clearSelection();
      this.selected.emit(this.selectedItem);
    }
    else {
      this.selectedItem = newSelection;
      this.listview.focus(index);
      this.selected.emit(this.selectedItem);
    }

    event.stopPropagation();

  }

  protected onClickListView(event: any) {
  }

  protected onChangeListView(event: any): void {
  }

  /*--------------------------- internal functions  --------------------------*/

  protected getRoles(): void {

    this.loading = true;

    const req: RequestRole = {
      licenseId: this.licenseId
      , moduleId: this.moduleId
    };

    this.adminService.getRole$(req)
      .pipe(takeUntil(this.destroy$)
        , tap(() => { this.loading = false; })
        , map((resp) => {

          const updatedItems: RoleChecked[] = resp.data?.map((item: Role) => ({
            ...item,
            checked: false,
          })) ?? [];

          return updatedItems;
        }))
      .subscribe(
        (items) => {

          this.dataItems = items;
        }
      );

  }

  protected setRole(item: RoleSave): void {

    const req: RequestRoleSet = {
      data: [item]
      , moduleId: this.moduleId
    };

    this.adminService.AddUpdate$(req).pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe((res) => {
        if (res.success && res.data && res.data?.length > 0) {
          const nRole = res.data[0];
          const existingRole = this.dataItems.find(item => item.id === nRole.id);

          if (!existingRole) {
            this.dataItems.push(nRole);
            this.notification.showNotification('Role added succesfully', 'success');
          }
          else {
            this.updateLocalRoleById(nRole);
            this.notification.showNotification('Role updated succesfully', 'success');
            this.editDataItem = null;
          }

          // Angular does not track changes to the collection elements
          // In order to fix the issue, we make a copy and reassign
          this.dataItems = [...this.dataItems];

        }
      });
  }

  protected deleteRole(roleId: string): void {
    const req: RequestDelete = {
      id: roleId
      , moduleId: this.moduleId
    };

    this.adminService.removeRole$(req)
      .pipe(takeUntil(this.destroy$)
        , finalize(() => this.loading = false))
      .subscribe((res) => {
        if (res.success) {
          this.removeLocalRoleById(roleId);
          this.clearSelection();

          this.dataItems = [...this.dataItems];

          this.notification.showNotification('Role deleted succesfully', 'success');
        }
        else {
          this.notification.showNotification(res.message ?? "Failed to delete role", 'error');
        }
      });
  }

  protected copyRole(item: RoleSave, name: string) {

    let citem: RoleSave = {
      copyRoleId: item.id
      , licenseId: item.licenseId
      , name: name
      , allLicenseUsers: false
      , roleType: item.roleType
    };

    const req: RequestRoleSet = {
      data: [citem]
      , moduleId: this.moduleId
    };

    this.adminService.AddUpdate$(req)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe((res) => {
        if (res.success && res.data && res.data?.length > 0) {
          const nRole = res.data[0];
          const existingRole = this.dataItems.find(item => item.id === nRole.id);

          this.dataItems = [...this.dataItems];

          if (!existingRole) {
            this.dataItems.push(nRole);
            this.notification.showNotification('Role added succesfully', 'success');
          }
          else {
            this.updateLocalRoleById(nRole);
            this.notification.showNotification('Role updated succesfully', 'success');
            this.editDataItem = null;
          }

        }
      });
  }

  protected createTemplateFromRole(item: RoleSave, name: string) {

    let tItem: RoleSave = {
      roleIdAsTemplate: item.id
      , name: name
      , roleType: item.roleType
    };

    const req: RequestRoleSet = {
      data: [tItem]
      , moduleId: this.moduleId
    };

    this.adminService.AddUpdate$(req)
      .pipe(takeUntil(this.destroy$), finalize(() => this.loading = false))
      .subscribe((res) => {
        if (res.success && res.data && res.data?.length > 0) {
          const nRole = res.data[0];
          const existingRole = this.dataItems.find(item => item.id === nRole.id);

          this.dataItems = [...this.dataItems];

          if (!existingRole) {
            this.dataItems.push(nRole);
            this.notification.showNotification('Role added succesfully', 'success');
          }
          else {
            this.updateLocalRoleById(nRole);
            this.notification.showNotification('Role updated succesfully', 'success');
            this.editDataItem = null;
          }
        }
      });
  }

  protected setShowEditor(condition: boolean): void {
    this.showEditor = condition;
  }

  protected updateLocalRoleById(updatedRole: Role): void {
    const index = this.dataItems.findIndex(item => item.id === updatedRole.id);
    if (index !== -1) {
      this.dataItems[index] = { ...this.dataItems[index], ...updatedRole };
    }
  }

  protected removeLocalRoleById(id: string): void {
    const index = this.dataItems.findIndex(item => item.id === id);
    if (index !== -1) {
      this.dataItems.splice(index, 1);
    }
  }

}


